import React from 'react';
import PropTypes from 'prop-types';

import classNames from 'classnames';
import { css, StyleSheet } from 'aphrodite';
import dayjs from 'dayjs';
import { faBuilding, faEdit } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Form, Table } from 'react-bootstrap';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';

import { backgroundGrey, grape, greyStroke, white } from 'lib/css/colors';
import Container from 'components/shared/Container';
import CopyableText from 'components/shared/CopyableText';
import { DATE_FORMAT_WITH_TIME } from 'lib/constants';
import EmptyState from 'components/shared/EmptyState';
import placeholderProductImage from 'images/placeholder-img-product-v2.svg';
import pointerOnHover from 'lib/css/pointerOnHover';
import VerificationStatusBadge from 'components/shared/VerificationStatusBadge';

function ManageManufacturersTable({
  manufacturers,
  mergeList,
  setMergeList,
  setManufacturerEditModalSettings,
}) {
  return (
    <Container>
      <Table className={css(styles.tableText)}>
        <thead className={css(styles.thead)}>
          <tr>
            <th>
              <Form.Check
                type="checkbox"
                checked={manufacturers?.length > 0 && mergeList?.length === manufacturers?.length}
                onChange={e => {
                  if (e.target.checked) {
                    setMergeList([...manufacturers]);
                  } else {
                    setMergeList([]);
                  }
                }}
              />
            </th>
            <th className={css(styles.logoHeader)}>Logo</th>
            <th>Manufacturer Name</th>
            <th>GLN</th>
            <th># of Brands</th>
            <th>Status</th>
            <th>Last Modified</th>
            <th>Created</th>
            <th />
          </tr>
        </thead>
        <tbody className={css(styles.tbody)}>
          {(!manufacturers || manufacturers.length === 0) && (
            <tr>
              <td colSpan={9}>
                <EmptyState
                  icon={faBuilding}
                  title="No manufacturers found"
                  description={
                    'Try adjusting your search or filter criteria ' +
                    "to find what you're looking for."
                  }
                />
              </td>
            </tr>
          )}
          {manufacturers?.map((manufacturer, index) => (
            <tr
              key={index}
              className={css(styles.clickableRow)}
              onClick={() =>
                window.open(`/enrich-products/manufacturer-details/${manufacturer.id}`, '_blank')
              }
            >
              <td onClick={e => e.stopPropagation()}>
                <Form.Check
                  type="checkbox"
                  checked={mergeList.some(
                    mergeManufacturer => mergeManufacturer.id === manufacturer.id,
                  )}
                  onChange={e => {
                    if (e.target.checked) {
                      if (
                        !mergeList.some(
                          mergeManufacturer => mergeManufacturer.id === manufacturer.id,
                        )
                      ) {
                        setMergeList([...mergeList, manufacturer]);
                      }
                    } else {
                      setMergeList(
                        mergeList.filter(
                          mergeManufacturer => mergeManufacturer.id !== manufacturer.id,
                        ),
                      );
                    }
                  }}
                />
              </td>
              <td className={css(styles.logoCell)}>
                <img
                  src={manufacturer.logo_url || placeholderProductImage}
                  alt={`${manufacturer.name} logo`}
                  className={css(styles.manufacturerLogo)}
                  onError={e => {
                    e.target.onerror = null;
                    e.target.src = placeholderProductImage;
                  }}
                />
              </td>
              <td>
                <CopyableText text={manufacturer.name} tooltipText="Copy name" />
              </td>
              <td>
                {manufacturer.manufacturer_glns &&
                Object.keys(manufacturer.manufacturer_glns).length > 0 ? (
                  <CopyableText
                    text={Object.keys(manufacturer.manufacturer_glns).join(', ')}
                    tooltipText="Copy GLN"
                  />
                ) : (
                  ''
                )}
              </td>
              <td>{manufacturer.brand_count}</td>
              <td>
                <VerificationStatusBadge status={manufacturer.verification_status} />
              </td>
              <td>{dayjs(manufacturer.date_modified).format(DATE_FORMAT_WITH_TIME)}</td>
              <td>{dayjs(manufacturer.date_created).format(DATE_FORMAT_WITH_TIME)}</td>
              <td className={css(styles.actionCell)}>
                <OverlayTrigger overlay={<Tooltip>Edit manufacturer</Tooltip>}>
                  <FontAwesomeIcon
                    icon={faEdit}
                    className={classNames(css(styles.editButton), pointerOnHover)}
                    onClick={e => {
                      e.stopPropagation();
                      setManufacturerEditModalSettings({
                        show: true,
                        manufacturer: manufacturer,
                      });
                    }}
                  />
                </OverlayTrigger>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky',
    top: 0,
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
  actionCell: {
    textAlign: 'center',
    width: 40,
  },
  editButton: {
    color: grape,
    fontSize: 16,
    ':hover': {
      opacity: 0.7,
    },
  },
  logoHeader: {
    width: 70,
    textAlign: 'center',
  },
  logoCell: {
    width: 70,
    padding: '4px 8px',
  },
  manufacturerLogo: {
    width: 48,
    height: 48,
    objectFit: 'contain',
    borderRadius: 6,
    border: `1px solid ${greyStroke}`,
    backgroundColor: white,
  },
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      backgroundColor: `${grape}10`,
    },
  },
});

ManageManufacturersTable.propTypes = {
  mergeList: PropTypes.array,
  setMergeList: PropTypes.func,
  manufacturers: PropTypes.array,
  setManufacturerEditModalSettings: PropTypes.func,
};

export default ManageManufacturersTable;
